import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-ee6387c0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "location-map-wrap" }
const _hoisted_2 = {
  key: 1,
  class: "map-state-tip map-state-tip--static"
}

import { onMounted, ref, watch, nextTick, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { formatTimeStamp } from '@/utils/time'
import {
  GoogleMapsMap,
  GoogleMapsMarker,
  GoogleMapsPolyline,
  GoogleMapsSdk,
  isGoogleMapsKeyConfigured,
  loadGoogleMaps
} from './google-maps'
import { buildMarkerIcon, formatPointTime, getMarkerPalette, loadLocationSvgText } from './marker-icon'
import { LocationInfoService, TrackResponseData } from './service'

const DEFAULT_MAP_ZOOM = 12
/** 无数据时地图默认中心（东京） */
const DEFAULT_MAP_CENTER_LAT = 35.681236
const DEFAULT_MAP_CENTER_LNG = 139.767125
const TRACK_HALO_WEIGHT = 7
const TRACK_HALO_OPACITY = 0.85
const TRACK_LINE_WEIGHT = 3.5
const TRACK_LINE_OPACITY = 0.9

interface TrackPoint {
  latitude: number
  longitude: number
  locationTime: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'location-map',
  props: {
  deviceId: {
    type: String,
    required: true
  },
  /** 时间范围（unix秒字符串），与历史列表共享同一筛选 */
  startTime: {
    type: String,
    default: undefined
  },
  endTime: {
    type: String,
    default: undefined
  },
  /** 父级抽屉是否全屏（全屏切换后需重算地图尺寸） */
  isFullscreen: {
    type: Boolean,
    default: false
  },
  /** 单点查看焦点（定位历史行点“在地图查看”进入）：非空时地图只居中展示该点，不拉取轨迹 */
  focusPoint: {
    type: Object as PropType<TrackPoint | null>,
    default: null
  },
  /** 轨迹采样密度（分钟），0=全部不传参；选择器在父级顶部筛选栏 */
  intervalMinute: {
    type: Number,
    default: 0
  }
},
  setup(__props, { expose: __expose }) {

const props = __props

const { t } = useI18n()
const injector = useReflectiveInjector([LocationInfoService])
const service = injector.get(LocationInfoService)

const isKeyConfigured = isGoogleMapsKeyConfigured()
const isLoading = ref(false)
const loadFailed = ref(false)
const noData = ref(false)
const mapContainer = ref<HTMLElement | null>(null)

let sdk: GoogleMapsSdk | null = null
let mapInstance: GoogleMapsMap | null = null
let mapMarkers: GoogleMapsMarker[] = []
let mapPolylines: GoogleMapsPolyline[] = []

onMounted(() => {
  void updateMap()
})

watch(() => [props.deviceId, props.startTime, props.endTime, props.focusPoint, props.intervalMinute], () => {
  void updateMap()
})

watch(() => props.isFullscreen, () => {
  // 抽屉宽度变化后地图容器尺寸改变，等布局完成再重算
  nextTick(() => handleVisible())
})

/** 拉取轨迹（单点查看模式跳过请求）并重绘地图 */
async function updateMap() {
  if (!isKeyConfigured || !props.deviceId) {
    return
  }
  isLoading.value = true
  loadFailed.value = false
  try {
    const [mapsSdk, pinSvgText, trackRes] = await Promise.all([
      loadGoogleMaps(),
      loadLocationSvgText(),
      props.focusPoint
        ? Promise.resolve(undefined)
        : service.getLocationTrack({
            device_id: props.deviceId,
            ...(props.startTime ? { start_time: props.startTime } : {}),
            ...(props.endTime ? { end_time: props.endTime } : {}),
            ...(props.intervalMinute > 0 ? { interval_minute: props.intervalMinute } : {})
          })
    ])
    sdk = mapsSdk
    ensureMapInstance()
    if (props.focusPoint) {
      drawFocusPoint(props.focusPoint, pinSvgText)
    } else if (trackRes) {
      drawAll(trackRes, pinSvgText)
    }
  } catch (error) {
    loadFailed.value = true
  } finally {
    isLoading.value = false
  }
}

/** 容器从 display:none 恢复显示后由父级调用，通知 SDK 重算尺寸 */
function handleVisible() {
  if (sdk?.event && mapInstance) {
    sdk.event.trigger(mapInstance, 'resize')
  }
}

__expose({ handleVisible })

/** 地图实例每个组件生命周期只创建一次（Google 不支持在同一容器重复 new Map） */
function ensureMapInstance() {
  if (mapInstance || !mapContainer.value || !sdk) {
    return
  }
  mapInstance = new sdk.Map(mapContainer.value, {
    zoom: DEFAULT_MAP_ZOOM,
    center: new sdk.LatLng(DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LNG),
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false
  })
}

function clearDrawings() {
  mapMarkers.forEach(marker => marker.setMap(null))
  mapPolylines.forEach(polyline => polyline.setMap(null))
  mapMarkers = []
  mapPolylines = []
}

function drawAll(trackRes: TrackResponseData, pinSvgText: string) {
  const maps = sdk
  const map = mapInstance
  if (!maps || !map) {
    return
  }
  clearDrawings()

  /** 轨迹点：过滤经纬度为空的记录 */
  const trackPoints = (trackRes.points ?? [])
    .filter(item => item.longitude && item.latitude)
    .map(item => ({
      latitude: Number(item.latitude),
      longitude: Number(item.longitude),
      locationTime: item.location_time
    }))

  noData.value = trackPoints.length === 0
  if (noData.value) {
    return
  }

  drawTrack(maps, map, trackPoints, pinSvgText)
}

/** 轨迹双层折线（白色描边底层 + 主题色主层）+ 每个轨迹点标点（location.svg + 钉下时间卡）；
 *  起终点额外绑定 InfoWindow；有轨迹时以完整轨迹视野为准，否则单点居中 */
function drawTrack(maps: GoogleMapsSdk, map: GoogleMapsMap, trackPoints: TrackPoint[], pinSvgText: string) {
  if (trackPoints.length >= 2) {
    const path = trackPoints.map(item => new maps.LatLng(item.latitude, item.longitude))
    const palette = getMarkerPalette()
    const trackHalo = new maps.Polyline({
      path,
      map,
      strokeColor: palette.cardBackground,
      strokeWeight: TRACK_HALO_WEIGHT,
      strokeOpacity: TRACK_HALO_OPACITY
    })
    const trackLine = new maps.Polyline({
      path,
      map,
      strokeColor: palette.primary,
      strokeWeight: TRACK_LINE_WEIGHT,
      strokeOpacity: TRACK_LINE_OPACITY
    })
    mapPolylines.push(trackHalo, trackLine)
    const bounds = new maps.LatLngBounds()
    trackPoints.forEach((point, index) => {
      const icon = buildMarkerIcon(maps, pinSvgText, { lines: [{ text: formatPointTime(point.locationTime), bold: true }] })
      // optimized:false 走 DOM 渲染，保证 data-URI SVG 图标稳定显示
      const marker = new maps.Marker({ position: path[index], map, icon, optimized: false })
      mapMarkers.push(marker)
      if (index === 0 || index === trackPoints.length - 1) {
        bindPointInfoWindow(maps, map, marker, point, index === 0)
      }
      bounds.extend(path[index])
    })
    map.fitBounds(bounds)
    return
  }
  if (trackPoints.length === 1) {
    const point = trackPoints[0]
    const marker = drawSinglePointMarker(maps, map, point, pinSvgText)
    bindPointInfoWindow(maps, map, marker, point, false)
  }
}

/** 单点查看模式：定位历史某一行在地图上居中展示（钉下时间卡，不拉取轨迹） */
function drawFocusPoint(point: TrackPoint, pinSvgText: string) {
  const maps = sdk
  const map = mapInstance
  if (!maps || !map) {
    return
  }
  clearDrawings()
  noData.value = !point.longitude || !point.latitude
  if (noData.value) {
    return
  }
  drawSinglePointMarker(maps, map, point, pinSvgText)
}

/** 单个轨迹点居中展示（轨迹仅一个点 & 单点查看共用），返回图钉供绑定 InfoWindow */
function drawSinglePointMarker(maps: GoogleMapsSdk, map: GoogleMapsMap, point: TrackPoint, pinSvgText: string): GoogleMapsMarker {
  const position = new maps.LatLng(point.latitude, point.longitude)
  const icon = buildMarkerIcon(maps, pinSvgText, { lines: [{ text: formatPointTime(point.locationTime), bold: true }] })
  const marker = new maps.Marker({ position, map, icon, optimized: false })
  mapMarkers.push(marker)
  map.setCenter(position)
  map.setZoom(DEFAULT_MAP_ZOOM)
  return marker
}

/** 轨迹起/终点 InfoWindow（展示定位时间） */
function bindPointInfoWindow(maps: GoogleMapsSdk, map: GoogleMapsMap, marker: GoogleMapsMarker, point: TrackPoint, isStart: boolean) {
  const infoWindow = new maps.InfoWindow({
    content: [
      t(isStart ? 'groupClient.locationInfoTrackStart' : 'groupClient.locationInfoTrackEnd'),
      `${t('groupClient.locationInfoColTime')}: ${formatTimeStamp(point.locationTime) || '--'}`
    ].join('<br/>')
  })
  marker.addListener('click', () => infoWindow.open(map, marker))
}


return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isKeyConfigured))
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", {
            ref_key: "mapContainer",
            ref: mapContainer,
            class: "map-container el-card"
          }, null, 512),
          _withDirectives(_createElementVNode("div", { class: "map-state-tip" }, _toDisplayString(_ctx.$t('groupClient.locationInfoMapLoadFailed')), 513), [
            [_vShow, loadFailed.value]
          ]),
          _withDirectives(_createElementVNode("div", { class: "map-state-tip" }, _toDisplayString(_ctx.$t('groupClient.locationInfoEmptyMap')), 513), [
            [_vShow, !loadFailed.value && noData.value]
          ])
        ], 64))
      : _createCommentVNode("", true),
    (!_unref(isKeyConfigured))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_ctx.$t('groupClient.locationInfoMapKeyMissing')), 1))
      : _createCommentVNode("", true)
  ])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})